import { type JSX, type ReactNode } from 'react';

import { I18nProvider } from './i18n';
import { ContentProvider } from './content/ContentProvider';
import { JTFooter } from './components/JTFooter';
import { JTHeader } from './components/JTHeader';
import { HomePage } from './HomePage';
import { CategoryPage } from './discovery/CategoryPage';
import { OfferPage } from './discovery/OfferPage';
import { SearchPage } from './discovery/SearchPage';
import { AppLink, RouterProvider, useNavigator } from './discovery/router';
import { usePageMeta } from './discovery/page-meta';
import { PennyVoicePage } from './penny/PennyVoicePage';
import { SessionProvider } from './session/SessionProvider';
import { LoginPage } from './site/LoginPage';
import { SitePage } from './site/SitePages';
import { StorePage } from './site/store/StorePage';
import './site/site.css';

/**
 * The application shell.
 *
 * ## `RouterProvider` is outermost, and everything else is per-page
 *
 * `HomePage` already owns its own chrome — the providers, the header, the
 * footer and the skip link — and its suite renders it directly and asserts all
 * four. Lifting that out would be a refactor of a page this phase is only
 * adding to, so the discovery pages get the same chrome through `DiscoveryShell`
 * instead. Four lines repeated, deliberately, rather than a change that would
 * ripple through fifty passing tests for no behavioural gain.
 *
 * ## An unmatched path is a real not-found page
 *
 * Previously any unknown path rendered the homepage, because nginx serves
 * `index.html` for everything and there was nothing to match against. A
 * mistyped URL looked like a working page, every wrong link told a crawler it
 * was valid, and broken navigation was invisible to us. Now it says so, and
 * says `noindex`.
 */
function CurrentPage(): JSX.Element {
  const { location } = useNavigator();

  switch (location.match.name) {
    case 'home':
      // Brings its own providers, header and footer.
      return <HomePage />;
    case 'search':
      return (
        <DiscoveryShell>
          <SearchPage />
        </DiscoveryShell>
      );
    case 'category':
      return (
        <DiscoveryShell>
          <CategoryPage categoryId={location.match.categoryId} />
        </DiscoveryShell>
      );
    case 'offer':
      return (
        <DiscoveryShell>
          <OfferPage listingId={location.match.listingId} />
        </DiscoveryShell>
      );
    case 'storefront':
      // Website stage 5 (2026-11-01): the Store frame replaces the old
      // discovery storefront on the same path, so every existing link to a
      // shop — the homepage's cards, search, product pages — lands on it. It
      // draws the frames' own header (with the location picker and basket).
      return <StorePage slug={location.match.slug} />;
    case 'pennyVoice':
      return (
        <DiscoveryShell>
          <PennyVoicePage />
        </DiscoveryShell>
      );
    case 'login':
      // The Log In frame draws its own minimal header and footer.
      return <LoginPage />;
    case 'site':
      // The website pages use the frames' header and footer (SiteShell), not
      // the homepage's — owner decision D1, 2026-11-01.
      return <SitePage page={location.match.page} />;
    case 'unknown':
      return (
        <DiscoveryShell>
          <NotFoundPage path={location.match.path} />
        </DiscoveryShell>
      );
  }
}

export function DiscoveryShell({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <I18nProvider>
      <ContentProvider>
        {/* Keyboard users should not have to tab the header to reach the page. */}
        <a className="jt-skip" href="#main">
          Skip to main content
        </a>

        <JTHeader />

        <main id="main">{children}</main>

        <JTFooter />
      </ContentProvider>
    </I18nProvider>
  );
}

function NotFoundPage({ path }: { readonly path: string }): JSX.Element {
  usePageMeta({
    title: 'Page not found',
    // Never indexable. A single-page app answers 200 for every path, so without
    // this a crawler files every typo as a real page.
    noindex: true,
  });

  return (
    <div className="jt-page jt-container">
      <h1 className="jt-page__title">We could not find that page.</h1>
      <p>
        <code className="jt-code">{path}</code> does not exist.{' '}
        <AppLink className="jt-link" to="/">
          Go to the homepage
        </AppLink>{' '}
        or{' '}
        <AppLink className="jt-link" to="/search">
          search the marketplace
        </AppLink>
        .
      </p>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <RouterProvider>
      {/*
        One session for every website page, so it survives client-side
        navigation. It asks the server nothing until a page that needs it
        mounts; the homepage never does.
      */}
      <SessionProvider>
        <CurrentPage />
      </SessionProvider>
    </RouterProvider>
  );
}
